import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { ScoreGaugeTarget } from '@sorare/core/src/components/lineup/ScoreGaugeTarget';
import { findLastIndex, sortBy } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isActiveAppearance } from '@sorare/core/src/lib/players';

import { TargetIcon } from '#src/assets/icons/TargetIcon.tsx';
import { targetColors } from '#src/components/leaderboard/ThresholdRewards/index.tsx';

import type {
  ThresholdProgressBar_so5Leaderboard,
  ThresholdProgressBar_so5Lineup,
} from './__generated__/index.graphql.ts';

type Props = {
  so5Leaderboard: ThresholdProgressBar_so5Leaderboard;
  so5Lineup?: Nullable<ThresholdProgressBar_so5Lineup>;
};

export const ThresholdProgressBar = ({ so5Leaderboard, so5Lineup }: Props) => {
  const { so5Rankings, rewardsConfig } = so5Leaderboard;

  if (!so5Rankings) {
    return null;
  }

  const so5Ranking = so5Lineup?.so5Rankings[0];
  const hasUpcomingGame = so5Lineup?.so5Appearances
    .filter(isActiveAppearance)
    .some(appearance => !!appearance.upcomingGame);

  const thresholds = sortBy(
    ({ score }) => score || 0,
    (rewardsConfig.conditional || []).filter(Boolean)
  );
  const initialThresholdIndex =
    thresholds
      ?.slice()
      .reverse()
      .findIndex(
        reward =>
          reward?.score && so5Ranking?.score && reward.score <= so5Ranking.score
      ) ?? 0;
  const currentThresholdIndex = findLastIndex(
    thresholds || [],
    t => (so5Ranking?.score ?? 0) >= (t?.score ?? 0)
  );

  const sectionsWidth = 100 / (thresholds.length + 1);

  const getCursorPosition = () => {
    if (!so5Ranking?.score || !thresholds.length) {
      return 0;
    }

    const currentScore = so5Ranking.score;

    // Find the threshold just below and above the current score

    const currentThreshold =
      currentThresholdIndex >= 0
        ? thresholds[currentThresholdIndex]
        : undefined;
    const nextThreshold = thresholds[currentThresholdIndex + 1];

    const sectionIndex = currentThreshold
      ? thresholds.indexOf(currentThreshold) + 1
      : 0;
    const startScore = currentThreshold?.score ?? 0;
    const endScore = nextThreshold?.score ?? so5Rankings.nodes[0].score;

    // Calculate base position for the section
    const basePosition = sectionIndex * sectionsWidth;

    // Calculate position within the section
    const scoreRange = endScore - startScore;
    const scoreProgress = currentScore - startScore;
    const sectionProgress = scoreRange > 0 ? scoreProgress / scoreRange : 0;

    return `calc(100% - ${basePosition + sectionProgress * sectionsWidth}%)`;
  };

  return (
    <ScoreGaugeTarget
      name={
        currentThresholdIndex >= 0 ? (
          <Horizontal>
            <TargetIcon color={targetColors[initialThresholdIndex]} />
            <LabelL bold>
              <FormattedMessage
                {...(thresholds.length === 1
                  ? fantasy.target
                  : fantasy.targetNumber)}
                values={{
                  targetNb: initialThresholdIndex + 1,
                }}
              />
            </LabelL>
          </Horizontal>
        ) : (
          <Horizontal>
            {hasUpcomingGame ? (
              <FontAwesomeIcon icon={iconHourglass} />
            ) : (
              <FontAwesomeIcon icon={iconX} color="var(--c-red-400)" />
            )}
            <LabelL bold>
              {hasUpcomingGame ? (
                <FormattedMessage defaultMessage="In Progress" />
              ) : (
                <FormattedMessage defaultMessage="Target Missed" />
              )}
            </LabelL>
          </Horizontal>
        )
      }
      currentScore={so5Ranking?.score ?? 0}
      sections={thresholds}
      cursorPosition={getCursorPosition() || '100%'}
    >
      {currentThresholdIndex + 1 < (thresholds?.length ?? 0) && (
        <LabelM color="var(--c-nd-600)" className="text-center">
          <FormattedMessage
            defaultMessage="<span>{points, number}pts</span> to next target"
            values={{
              points: Math.ceil(
                (thresholds?.[(currentThresholdIndex ?? 0) + 1]?.score ?? 0) -
                  (so5Ranking?.score ?? 0)
              ),
              span: (...chunks) => (
                <span
                  style={{
                    color: 'var(--c-nl-100-opaque)',
                    fontWeight: 'bold',
                  }}
                >
                  {chunks}
                </span>
              ),
            }}
          />
        </LabelM>
      )}
    </ScoreGaugeTarget>
  );
};

ThresholdProgressBar.fragments = {
  so5Leaderboard: gql`
    fragment ThresholdProgressBar_so5Leaderboard on So5Leaderboard {
      slug
      so5Rankings(first: 1) {
        nodes {
          id
          score
        }
      }
      rewardsConfig {
        conditional {
          id
          score
        }
      }
    }
  ` as TypedDocumentNode<ThresholdProgressBar_so5Leaderboard>,
  so5Lineup: gql`
    fragment ThresholdProgressBar_so5Lineup on So5Lineup {
      id
      so5Appearances(includeSubs: true) {
        substitutionState
        id
        upcomingGame {
          id
        }
      }
      so5Rankings {
        id
        score
      }
    }
  ` as TypedDocumentNode<ThresholdProgressBar_so5Lineup>,
};
